import { StopLoss } from '@App/interfaces/CommonInterfaces'
import { Tabs } from '@App/ui/crypto_exchange/desktop/components/common/Tabs'
import { TickerPrice } from '@App/ui/crypto_exchange/desktop/pages/discover/components/MostVolatile/components/TickerPrice'

import { Trade } from '@Redux/slices/trade/trades/types'
import { TradeOrderConfirmBtn } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/TradeOrderConfirmBtn'
import {
  ITradeBlockProps,
  ORDER_ACTION_TYPE,
} from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/types'

import { TradeInput } from '../../../TradeInput'
import {
  COMMON_FIELD_NAMES,
  roundNumber,
  StopLossField,
  TakeProfitField,
  useOrderData,
  useOrderForm,
  useOrderFormCalculations,
} from '../../model'
import { Divider } from '../Divider'
import { InputHint } from '../InputHint'
import { LimitOrderBlockView } from '../LimitOrderBlockView'
import { ProfitAndLoss } from '../ProfitAndLoss'
import { SeparateLine } from '../SeparateLine'
import { StopLossFields } from '../StopLossFields'
import { TakeProfitFields } from '../TakeProfitFields'

import { AmountToClose } from './components/AmountToClose'
import { useCloseOrder } from './components/AmountToClose/model'
import { GeneralInfo } from './components/GeneralInfo'
import { PositionDetailsPopUp } from './components/PositionDetailsPopUp'
import { TabsLayout } from './components/TabsLayout'
import { TABS, useEditTrade } from './model'
import classes from './styles.module.scss'

export const EditTrade = ({
  current_market,
  operationType,
  order,
  orderType,
}: ITradeBlockProps & { operationType: ORDER_ACTION_TYPE; order: Trade }) => {
  const { target_currency, source_currency, amount_precision, price_precision } = current_market
  const LEVERAGE = order.leverage
  const {
    wallet,
    walletSymbol,
    tickerDataPrice,
    tickerToWalletCurrencyPrice,
    isLogin,
    marketRule,
    oppositeTickerDataPrice,
  } = useOrderData({
    source_currency,
    target_currency,
    operationType,
  })

  const {
    register,
    formState,
    setValue,
    getValues,
    handleSubmit,
    stopLossType,
    stopLossAmount,
    stopLossDistance,
    stopLossDistancePerCent,
    stopLossLevel,
    takeProfitAmount,
    takeProfitDistance,
    takeProfitDistancePerCent,
    takeProfitLevel,
    takeProfitActive,
    stopLossField,
    setStopLossField,
    takeProfitField,
    setTakeProfitField,
    setTakeProfitActive,
    setStopLossType,
    reset,
    getStopLossValues,
    getTakeProfitValues,
  } = useOrderForm({
    operationType,
    amountPrecision: amount_precision,
    pricePrecision: price_precision,
    tickerDataPrice: order.open_rate,
    marketRule,
    oppositeTickerDataPrice,
    tickerToWalletCurrencyPrice,
    isEdit: true
  })

  const {
    calculateTakeProfitValuesByActiveField,
    calculateTakeProfitValuesInit,
    calculateStopLossValuesByActiveField,
    calculateStopLossValuesInit,
  } = useOrderFormCalculations({
    tickerDataPrice: order.open_rate,
    tickerToWalletCurrencyPrice,
    getValues,
    setValue,
    leverage: LEVERAGE,
    takeProfitField,
    stopLossField,
    walletAmount: wallet?.amount,
    operationType,
    marketRule,
    oppositeTickerDataPrice,
    amountPrecision: amount_precision,
    pricePrecision: price_precision,
    isEdit: true,
  })

  const { onSubmit, activeTab, setActiveTab, pnl } = useEditTrade({
    order,
    wallet,
    orderType,
    getValues,
    setValue,
    reset,
    tickerDataPrice,
    getStopLossValues,
    getTakeProfitValues
  })

  const { closeOrder, targetAmount } = useCloseOrder(order, tickerDataPrice)

  return (
    <>
      <LimitOrderBlockView
        isLogin={isLogin}
        current_market={current_market}
        formBtn={
          <>
            {activeTab === TABS[0].value && (
              <TradeOrderConfirmBtn
                type={operationType}
                activeMarginTab={''}
                source_currency={source_currency}
                target_currency={target_currency}
                sendMessage={handleSubmit(onSubmit)}
              >
                Update position
              </TradeOrderConfirmBtn>
            )}
            {activeTab === TABS[1].value && (
              <TradeOrderConfirmBtn
                type={operationType}
                activeMarginTab={''}
                source_currency={source_currency}
                target_currency={target_currency}
                sendMessage={closeOrder}
              >
                Close to {targetAmount}
              </TradeOrderConfirmBtn>
            )}
          </>
        }
      >
        <TickerPrice price={oppositeTickerDataPrice} classNames={classes.tickerValue}>
          {oppositeTickerDataPrice}
        </TickerPrice>
        <ProfitAndLoss
          quantity={order.quantity}
          open_rate={order.open_rate}
          action_type={order.action_type}
          currentPrice={oppositeTickerDataPrice}
          currency={order.currency}
        />
        <GeneralInfo
          quantity={order.quantity}
          tickerDataPrice={tickerDataPrice}
          leverage={LEVERAGE ?? 1}
          operationType={operationType}
          currency={order.currency}
          renderDetailsPopUp={(margin) => (
            <PositionDetailsPopUp
              leverage={LEVERAGE ?? 0}
              currency={order.currency}
              margin={margin}
              targetPrice={order.amount}
            />
          )}
        />
        <SeparateLine />
        <TabsLayout
          selector={
            <Tabs
              activeTab={activeTab}
              tabs={TABS}
              setActiveTab={setActiveTab}
              tabsFillSpace
              tabsClassName={classes.tabsSelector}
            />
          }
        >
          {activeTab === TABS[0].value && (
            <>
              <StopLossFields
                stopLossField={stopLossField}
                onOpenChange={(val) => setStopLossType(val ? StopLoss.Normal : null, calculateStopLossValuesInit)}
                stopLossType={stopLossType}
                onStopLossFieldChange={(val) => setStopLossField(val as StopLossField)}
                onStopLossTypeChange={(val) => setStopLossType(val as StopLoss, calculateStopLossValuesInit)}
                input={
                  <TradeInput
                    key={stopLossField}
                    type="number"
                    showControls
                    {...register(stopLossField, { valueAsNumber: true })}
                    onUpdateValue={() => {
                      calculateStopLossValuesByActiveField()
                    }}
                    targetCurrency={stopLossField === 'stop_loss_amount' ? walletSymbol : ''}
                    error={formState.errors[stopLossField]?.message}
                    subValue={
                      stopLossField === 'stop_loss_distance'
                        ? `${roundNumber(stopLossDistancePerCent ?? 0, 3)}%`
                        : undefined
                    }
                  />
                }
                inputHint={
                  <InputHint
                    distance={stopLossDistance}
                    distancePerCent={stopLossDistancePerCent}
                    amount={stopLossAmount}
                    level={stopLossLevel}
                    walletSymbol={walletSymbol}
                    activeField={COMMON_FIELD_NAMES[stopLossField]}
                  />
                }
              />
              <Divider />
              <TakeProfitFields
                onOpenChange={(val) => setTakeProfitActive(val, calculateTakeProfitValuesInit)}
                takeProfitField={takeProfitField}
                takeProfitActive={takeProfitActive}
                onTakeProfitFieldChange={(val) => setTakeProfitField(val as TakeProfitField)}
                input={
                  <TradeInput
                    key={takeProfitField}
                    type="number"
                    showControls
                    targetCurrency={takeProfitField === 'take_profit_amount' ? walletSymbol : ''}
                    {...register(takeProfitField, { valueAsNumber: true })}
                    onUpdateValue={() => {
                      calculateTakeProfitValuesByActiveField()
                    }}
                    error={formState.errors[takeProfitField]?.message}
                    subValue={
                      takeProfitField === 'take_profit_distance'
                        ? `${roundNumber(takeProfitDistancePerCent ?? 0, 3)}%`
                        : undefined
                    }
                  />
                }
                inputHint={
                  <InputHint
                    distance={takeProfitDistance}
                    distancePerCent={takeProfitDistancePerCent}
                    amount={takeProfitAmount}
                    level={takeProfitLevel}
                    walletSymbol={walletSymbol}
                    activeField={COMMON_FIELD_NAMES[takeProfitField]}
                    amountFieldName="Profit"
                  />
                }
              />
              <Divider />
            </>
          )}
          {activeTab === TABS[1].value && <AmountToClose pnl={pnl} currency={order.currency} />}
        </TabsLayout>
      </LimitOrderBlockView>
    </>
  )
}
